// Pure helpers for the AHA-6681 tile-panel presentation of
// RecommendationsStrip (variant='tiles'): have component tiles, the derived
// readout bar, want system-type tiles with adaptive density, and
// recommendation attribution dots. SQL/UI-free so every rule here is
// unit-testable; the state machinery (fingerprint precedence, overwrite,
// want seed) stays in recommendations-strip.tsx.

import {
  DuctedFuelToken,
  systemTypeCodeFuel
} from 'utils/kept-system-type-annotation';
import {
  ScenarioRecommendation,
  SystemTypeOption,
  WantRecommendationGroup
} from 'utils/recommendations-strip-logic';
import {
  DWELLING_EXCLUDED_BLOWER_CODE,
  FUEL_TILE_CODES,
  HAVE_TILES
} from './component-catalog';
import {
  BlowerDwelling,
  HaveSection,
  HaveTile,
  TileFuel
} from './component-catalog-types';

export type {
  BlowerDwelling,
  HaveSection,
  HaveTile,
  TileFuel
} from './component-catalog-types';
export { FUEL_TILE_CODES, HAVE_TILES } from './component-catalog';

export const HAVE_SECTIONS: HaveSection[] = [
  'Outdoor',
  'Indoor',
  'Blower',
  'Packaged'
];

const TILE_CODE_SET = new Set(HAVE_TILES.map((tile) => tile.code));

// Every fuel variant of a code shares its section, so the code alone names
// the section (and the derivation slot the exclusion rules reason about).
const SECTION_BY_CODE: Record<string, HaveSection> = {};
for (const tile of HAVE_TILES) {
  SECTION_BY_CODE[tile.code] = tile.section;
}

// Geoff ruling (AHA-6681 dwelling filter): the Blower offer follows the
// structure's dwelling type. Mobile homes are offered only mobile-home
// furnaces + the Modular Blower; standard homes (including a null/unknown
// subtype — Geoff's explicit rule) only standard furnaces + MB; modular
// homes see ALL Blower options (for now). Offer-only, exactly the
// hiddenFromOffer shape: persisted selections still render collapsed,
// radio-replace, and deselect. The excluded-code Record lives on
// COMPONENT_CATALOG so the want picker cannot drift into a second encoding.

// Maps a structure-subtype CODE to the Blower dwelling mode — code-keyed,
// never id-keyed (the resolveDwellingSuggestion precedent). Working
// assumption flagged to Geoff (unrefuted): MANUFACTURED HOME gets the
// mobile-home treatment. Anything else — unknown code, null, undefined
// (structure types not loaded yet) — is 'standard'.
// EXACT TWIN of the server's dwellingForStructureSubtypeCode in
// system-selector-api/src/utils/derive-current-system.ts (AHA-6681
// mobile-home derivation) — keep the literals in lockstep, both files
// cross-reference.
export function dwellingForSubtypeCode(
  code: string | null | undefined
): BlowerDwelling {
  if (code === 'mobile_home' || code === 'manufactured_home') {
    return 'mobile';
  }
  if (code === 'modular_home') {
    return 'modular';
  }
  return 'standard';
}

const normalizeFuel = (fuel: string | null): TileFuel =>
  fuel === 'NG' || fuel === 'LP' || fuel === 'Oil' || fuel === 'Electric'
    ? fuel
    : null;

// SELECTION-STATE INVARIANT (v1.1): state stays the persisted shape —
// virtualCodes: string[] + one shared virtualFuel — because every
// fuel-bearing tile (UF×3, MF×4) lives in the Blower section and
// toggleHaveSelection enforces single-select per section, so at most ONE
// fuel-bearing code is ever selected and virtualFuel unambiguously names
// its variant. Legacy persisted records predating v1.1 can violate this
// (multi-code sections, AH+UF, a fuel-null furnace from an old "Not sure"
// save): the section views below tolerate them read-only, and the first
// gesture in a section normalizes that section via the replace semantics.

// A tile identity reference: (code, fuel variant). The canvas passes these
// back on click; a fuel-null furnace reference only arises from the
// fuel-unknown fallback representation of a legacy record.
export type HaveTileRef = { code: string; fuel: TileFuel };

const refSelected = (
  codes: string[],
  fuel: string | null,
  ref: HaveTileRef
): boolean =>
  codes.includes(ref.code) &&
  (!FUEL_TILE_CODES.has(ref.code) || normalizeFuel(fuel) === ref.fuel);

// Radio semantics per section: selecting replaces anything else in the
// target section (other sections untouched); clicking the selected tile
// deselects it. Cross-section conflicts (packaged vs split, AH vs blower)
// are prevented by the section VISIBILITY rules in computeHaveSectionViews,
// not by clearing here — this deliberately replaces the old toggleHaveCode
// packaged-clears-splits behavior. virtualFuel follows the fuel-bearing
// selection: set by selecting a fuel variant, cleared when no fuel-bearing
// code remains (so a stale literal never filters a later derive).
export function toggleHaveSelection(
  codes: string[],
  fuel: string | null,
  ref: HaveTileRef
): { codes: string[]; fuel: string | null } {
  if (refSelected(codes, fuel, ref)) {
    const nextCodes = codes.filter((code) => code !== ref.code);
    return {
      codes: nextCodes,
      fuel: nextCodes.some((code) => FUEL_TILE_CODES.has(code)) ? fuel : null
    };
  }
  const nextCodes = [
    ...codes.filter(
      (code) => SECTION_BY_CODE[code] !== SECTION_BY_CODE[ref.code]
    ),
    ref.code
  ];
  let nextFuel: string | null;
  if (FUEL_TILE_CODES.has(ref.code)) {
    nextFuel = ref.fuel;
  } else {
    nextFuel = nextCodes.some((code) => FUEL_TILE_CODES.has(code))
      ? fuel
      : null;
  }
  return { codes: nextCodes, fuel: nextFuel };
}

// A selected code's render representation: the exact tile when (code, fuel)
// maps onto one, else the honest fuel-unknown fallback — a furnace code
// with no fuel variant selected (legacy "Not sure" save, or a locked
// union-derived furnace whose fingerprint carries no fuel). The fallback
// renders as the section's collapsed selection and deselects on click
// (unlocked mode), keeping the legacy state escapable.
export type SelectedTileView =
  | { kind: 'tile'; tile: HaveTile }
  | { kind: 'fuel-unknown'; ref: HaveTileRef; label: string };

const FUEL_UNKNOWN_LABELS: Record<string, string> = {
  UF: 'Furnace — fuel not specified',
  MF: 'Mobile-Home Furnace — fuel not specified'
};

function selectedViewFor(
  code: string,
  fuel: string | null
): SelectedTileView | null {
  const variant = FUEL_TILE_CODES.has(code) ? normalizeFuel(fuel) : null;
  const tile = HAVE_TILES.find((t) => t.code === code && t.fuel === variant);
  if (tile) return { kind: 'tile', tile };
  if (FUEL_TILE_CODES.has(code)) {
    return {
      kind: 'fuel-unknown',
      ref: { code, fuel: null },
      label: FUEL_UNKNOWN_LABELS[code]
    };
  }
  return null;
}

export type HaveSectionView = {
  section: HaveSection;
  // Excluded section: render nothing, not even the header. Exclusion is a
  // pure function of the selected CODES so it composes with locked
  // (equipment-backed) canvases for free.
  hidden: boolean;
  // Non-empty = collapsed: render ONLY these under the header. Usually 0-1
  // per the invariant; legacy multi-code records render all their
  // selections honestly.
  selected: SelectedTileView[];
  // The expanded offer (already exclusion-filtered); [] when collapsed
  // or hidden.
  tiles: HaveTile[];
};

// AH/DH occupy the same derivation slot as the blower codes (AH+furnace
// matches zero types) — hidden within Indoor while a blower is selected.
const FANLESS_INDOOR_EXCLUDED = new Set(['AH', 'DH']);

// The sectioned canvas view (v1.1 §3-§7), computed from selection state:
// - collapse-on-select: a section with a selection shows only it;
// - all-in-one symmetric exclusion: any split selection hides Packaged,
//   a packaged selection hides all three split sections;
// - AH/blower slot exclusion: AH/DH hides the Blower section; any blower
//   selection hides the AH/DH tiles within Indoor (coils stay);
// - dwelling filter: the Blower offer excludes UF (mobile) or MF
//   (standard) per the Geoff ruling above; defaulting to 'standard'
//   means a caller that forgets the argument can never offer mobile-home
//   furnaces to an unknown dwelling.
// A section is never hidden while it holds a selection (legacy records can
// pair AH with a furnace): its selection renders honestly, deselectable.
export function computeHaveSectionViews(
  codes: string[],
  fuel: string | null,
  dwelling: BlowerDwelling = 'standard'
): HaveSectionView[] {
  const selectedBySection = new Map<HaveSection, SelectedTileView[]>();
  for (const code of codes) {
    const section = SECTION_BY_CODE[code];
    // Unknown persisted codes drop (defensive against garbage records).
    const view = section ? selectedViewFor(code, fuel) : null;
    if (section && view) {
      const list = selectedBySection.get(section) ?? [];
      list.push(view);
      selectedBySection.set(section, list);
    }
  }
  const anyPackaged = codes.some((c) => SECTION_BY_CODE[c] === 'Packaged');
  const anySplit = codes.some(
    (c) => SECTION_BY_CODE[c] != null && SECTION_BY_CODE[c] !== 'Packaged'
  );
  const anyBlower = codes.some((c) => SECTION_BY_CODE[c] === 'Blower');
  const airHandlerSelected = codes.includes('AH') || codes.includes('DH');
  return HAVE_SECTIONS.map((section) => {
    const selected = selectedBySection.get(section) ?? [];
    let hidden = false;
    if (selected.length === 0) {
      if (section === 'Packaged') {
        hidden = anySplit;
      } else {
        hidden = anyPackaged || (section === 'Blower' && airHandlerSelected);
      }
    }
    let tiles: HaveTile[] = [];
    if (!hidden && selected.length === 0) {
      tiles = HAVE_TILES.filter(
        (tile) =>
          tile.section === section &&
          !tile.hiddenFromOffer &&
          tile.code !== DWELLING_EXCLUDED_BLOWER_CODE[dwelling] &&
          !(
            section === 'Indoor' &&
            anyBlower &&
            FANLESS_INDOOR_EXCLUDED.has(tile.code)
          )
      );
    }
    return { section, hidden, selected, tiles };
  });
}

// Maps the persisted fuel literal to the derive-current-system fuelType.
// 'Electric' and null (legacy "Not sure" saves) post no filter — they ride
// the union.
export function fuelTypeForDerive(
  virtualFuel: string | null
): 'NG' | 'LP' | 'Oil' | null {
  return virtualFuel === 'NG' || virtualFuel === 'LP' || virtualFuel === 'Oil'
    ? virtualFuel
    : null;
}

export type FingerprintInventory = {
  codes: string[];
  fuel: 'NG' | 'LP' | 'Oil' | null;
};

// Parses the derivation fingerprint
// (`v1|<sorted slotted codes CSV>|<NG|LP|Oil|''>|<refrigerant>|<replacing CSV>`)
// into the tile vocabulary so equipment-backed canvases render locked tiles
// without a second derivation. DF→UF and UC→SC map slotted alternatives onto
// their tile; unknown codes drop. Null (absent/old-API fingerprint) means
// the tile canvas cannot render — the strip degrades to readout-only (§F).
export function parseFingerprintInventory(
  fingerprint: string | null | undefined
): FingerprintInventory | null {
  if (!fingerprint) return null;
  const segments = fingerprint.split('|');
  if (segments[0] !== 'v1' || segments.length < 3) return null;
  const slotAliases: Record<string, string> = { DF: 'UF', UC: 'SC' };
  const codes: string[] = [];
  for (const raw of segments[1].split(',')) {
    const code = slotAliases[raw] ?? raw;
    if (TILE_CODE_SET.has(code) && !codes.includes(code)) {
      codes.push(code);
    }
  }
  const fuel = segments[2];
  return {
    codes,
    fuel: fuel === 'NG' || fuel === 'LP' || fuel === 'Oil' ? fuel : null
  };
}

export type WantLabel = {
  line1: string;
  line2: string;
};

// Two-line want label (§G, now the recommendations-list row/legend label):
// line 1 = outdoor unit + heat source (the first two ' + ' segments),
// line 2 = the remainder. 'Air Conditioner' abbreviates to 'AC' on line 1
// only when composited (unambiguous there); the full composite name
// belongs in the row's title attribute.
export function splitWantLabel(name: string): WantLabel {
  const segments = name.split(' + ');
  if (segments.length <= 1) {
    return { line1: name, line2: '' };
  }
  const line1Segments = segments
    .slice(0, 2)
    .map((segment) => (segment === 'Air Conditioner' ? 'AC' : segment));
  const rest = segments.slice(2);
  return {
    line1: line1Segments.join(' + '),
    line2: rest.length > 0 ? `+ ${rest.join(' + ')}` : ''
  };
}

// A type's fuel-match under a fuel selection (AHA-6796 EE fuel row): it
// matches when ANY of its compatibility codes classifies to a selected
// fuel (systemTypeCodeFuel — the one fuel grammar, no second parser). No
// codes at all = UNMAPPED, deliberately a match: codeless types/recs stay
// visible (the "Not searchable" pill convention — hiding them would make
// the narrow lie about what exists).
function codesMatchFuels(
  codes: string[] | undefined,
  fuels: Set<DuctedFuelToken>
): boolean {
  if (!codes || codes.length === 0) return true;
  return codes.some((code) => fuels.has(systemTypeCodeFuel(code)));
}

// DISPLAY-ONLY fuel narrowing of the flat recommendations list (AHA-6796
// EE fuel row): unmapped recommendations (empty compatibilityCodes) stay
// visible with their existing "Not searchable" pill. Empty fuels = the
// original reference.
export function filterRecommendationsByFuel(
  recommendations: ScenarioRecommendation[],
  fuels: DuctedFuelToken[]
): ScenarioRecommendation[] {
  if (fuels.length === 0) return recommendations;
  const selectedFuels = new Set(fuels);
  const filtered = recommendations.filter((rec) =>
    codesMatchFuels(rec.compatibilityCodes, selectedFuels)
  );
  return filtered.length === recommendations.length
    ? recommendations
    : filtered;
}

// The want universe the tiles-variant component picker (and its
// composition index) builds from — always active (AHA-6681 amendment B):
// the have-keyed desiredOptions float first (seeding, attribution and recs
// stay scenario-bounded), then the remaining full catalog so browse mode
// works with no have and a seeded/manual want outside the scenario set is
// never unrepresentable. Dedupe by id — on a live API desiredOptions is a
// subset of the catalog. desiredOptions entries lack compatibilityCodes;
// hydrate them from the catalog by id so the index can key off codes.
export function buildWantCanvasOptions(
  desiredOptions: SystemTypeOption[] | null | undefined,
  orderedCatalog: SystemTypeOption[] | null | undefined
): SystemTypeOption[] {
  const codesById = new Map<number, string[]>();
  for (const type of orderedCatalog ?? []) {
    if (type.compatibilityCodes && type.compatibilityCodes.length > 0) {
      codesById.set(type.id, type.compatibilityCodes);
    }
  }
  const hydrateCodes = (option: SystemTypeOption): SystemTypeOption => {
    if (option.compatibilityCodes && option.compatibilityCodes.length > 0) {
      return option;
    }
    const codes = codesById.get(option.id);
    if (!codes) return option;
    return { ...option, compatibilityCodes: codes };
  };
  const options = (desiredOptions ?? []).map(hydrateCodes);
  const seen = new Set(options.map((option) => option.id));
  for (const option of orderedCatalog ?? []) {
    if (!seen.has(option.id)) {
      seen.add(option.id);
      options.push(option);
    }
  }
  return options;
}

// Attribution dot palette: color = index of the want in the SELECTION order
// (wantIds), cycling past 8. Tailwind classes so the dots ride the app's
// styling system.
export const WANT_DOT_COLORS = [
  'bg-blue-600',
  'bg-purple-600',
  'bg-emerald-600',
  'bg-amber-500',
  'bg-rose-500',
  'bg-cyan-600',
  'bg-lime-600',
  'bg-fuchsia-600'
] as const;

export function wantDotColor(wantIds: number[], wantId: number): string {
  const index = wantIds.indexOf(wantId);
  return WANT_DOT_COLORS[(index >= 0 ? index : 0) % WANT_DOT_COLORS.length];
}

// systemTypeId → the wantIds whose per-want groups recommended it, in group
// (= want-selection) order. Feeds the per-row attribution dots on the flat
// deduped list (§J) — the per-want groups stay in the payload for exactly
// this.
export function buildAttributionMap(
  recommendationsByWant: WantRecommendationGroup[] | null | undefined
): Map<number, number[]> {
  const map = new Map<number, number[]>();
  for (const group of recommendationsByWant ?? []) {
    for (const rec of group.recommendations ?? []) {
      const wantIds = map.get(rec.systemTypeId) ?? [];
      if (!wantIds.includes(group.desiredSystemTypeId)) {
        wantIds.push(group.desiredSystemTypeId);
      }
      map.set(rec.systemTypeId, wantIds);
    }
  }
  return map;
}

// Fallback flat view for an old API response without the deduped
// `recommendations` field: one row per systemTypeId, first occurrence wins
// (same rule as the server's flat field).
export function dedupeRecommendationsById(
  recommendations: ScenarioRecommendation[]
): ScenarioRecommendation[] {
  const seen = new Set<number>();
  return recommendations.filter((rec) => {
    if (seen.has(rec.systemTypeId)) return false;
    seen.add(rec.systemTypeId);
    return true;
  });
}

export type ReadoutState =
  | { kind: 'empty' }
  | { kind: 'legacy-saved-pick'; name: string }
  | { kind: 'single'; name: string; locked: boolean }
  | { kind: 'union'; names: string[]; locked: boolean };

export type ComputeReadoutInput = {
  // Non-null when a fingerprint-matched legacy record (haveId, no
  // virtualCodes) anchors the recs — readout-only "Saved pick" state (§F).
  legacySavedPickId: number | null;
  // Fingerprint inventory has slotted codes: the canvas is equipment-backed
  // (locked tiles) and the readout carries the lock badge.
  equipmentBacked: boolean;
  // Any virtual tile currently selected (drives empty vs union-of-zero).
  hasTileSelection: boolean;
  haveId: number | null;
  candidateIds: number[];
  systemTypes: SystemTypeOption[];
};

// The derived-type readout bar's discriminated state (§B/§F): it replaces
// the have dropdown as the composite-type display.
export function computeReadout({
  legacySavedPickId,
  equipmentBacked,
  hasTileSelection,
  haveId,
  candidateIds,
  systemTypes
}: ComputeReadoutInput): ReadoutState {
  const nameOf = (id: number) =>
    systemTypes.find((type) => type.id === id)?.name ?? '';
  if (legacySavedPickId != null) {
    return { kind: 'legacy-saved-pick', name: nameOf(legacySavedPickId) };
  }
  if (!equipmentBacked && !hasTileSelection) {
    return { kind: 'empty' };
  }
  if (haveId != null) {
    return { kind: 'single', name: nameOf(haveId), locked: equipmentBacked };
  }
  return {
    kind: 'union',
    names: candidateIds.map(nameOf).filter((name) => name !== ''),
    locked: equipmentBacked
  };
}
